import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Account, Event, Metrics, ClientConfig } from '../types';

/**
 * LogoPulse API Service
 * Axios-based HTTP client for backend API calls
 *
 * Auth model: every request asks Amplify for the current session. Amplify
 * refreshes the ID token silently through the refresh token, so the bearer
 * token is never stale. On a 401 we force one refresh and retry once; only
 * if that fails do we sign the user out.
 */

export const API_URL = process.env.REACT_APP_API_URL || 'https://api.logopulse.io';

/** Listeners notified when the session is terminated by a 401 (used to clear caches). */
const sessionExpiredListeners: Array<() => void> = [];
export function onSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.push(listener);
  return () => {
    const idx = sessionExpiredListeners.indexOf(listener);
    if (idx >= 0) sessionExpiredListeners.splice(idx, 1);
  };
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried401?: boolean;
}

class ApiService {
  private client: AxiosInstance;
  private cachedToken: string | null = null;
  private cachedTokenExp = 0;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Attach a fresh ID token to every request
    this.client.interceptors.request.use(
      async (config) => {
        const token = await this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // On 401: force one token refresh and retry once, then sign out
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const original = error.config as RetryableConfig | undefined;
        if (error.response?.status === 401 && original && !original._retried401) {
          original._retried401 = true;
          const token = await this.getAuthToken(true);
          if (token) {
            original.headers.Authorization = `Bearer ${token}`;
            return this.client.request(original);
          }
          await this.handleSessionExpired();
        } else if (error.response?.status === 401) {
          await this.handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get a current ID token from Amplify. Cached in memory until shortly
   * before expiry; never written to localStorage.
   */
  async getAuthToken(forceRefresh = false): Promise<string | null> {
    const now = Date.now();
    if (!forceRefresh && this.cachedToken && now < this.cachedTokenExp - 60_000) {
      return this.cachedToken;
    }
    try {
      const session = await fetchAuthSession({ forceRefresh });
      const idToken = session.tokens?.idToken;
      if (!idToken) {
        this.cachedToken = null;
        return null;
      }
      this.cachedToken = idToken.toString();
      const exp = (idToken.payload?.exp as number | undefined) ?? 0;
      this.cachedTokenExp = exp ? exp * 1000 : now + 5 * 60_000;
      return this.cachedToken;
    } catch (error) {
      this.cachedToken = null;
      return null;
    }
  }

  /**
   * Drop the in-memory token (call on logout).
   */
  clearAuthToken(): void {
    this.cachedToken = null;
    this.cachedTokenExp = 0;
  }

  private async handleSessionExpired(): Promise<void> {
    this.clearAuthToken();
    sessionExpiredListeners.forEach((fn) => {
      try { fn(); } catch { /* ignore */ }
    });
    try { await signOut(); } catch { /* ignore */ }
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // ============================================================================
  // Accounts API
  // ============================================================================

  /**
   * List accounts for the authenticated user's org (server-side paginated).
   * Mirrors GET /v1/clients?stage=&search=&sortBy=&sortOrder=&page=&perPage=
   */
  async listAccounts(params: {
    page?: number;
    perPage?: number;
    search?: string;
    stage?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** exclude (default) | only | include */
    archived?: 'exclude' | 'only' | 'include';
  } = {}): Promise<{
    accounts: Account[];
    total: number;
    page: number;
    perPage: number;
    totalAccounts: number;
    archivedCount: number;
    hiddenCount: number;
    accountLimit: number;
    plan: string;
  }> {
    const query: Record<string, string | number> = {};
    if (params.page) query.page = params.page;
    if (params.perPage) query.perPage = params.perPage;
    if (params.search) query.search = params.search;
    if (params.stage && params.stage !== 'all') query.stage = params.stage;
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.sortOrder) query.sortOrder = params.sortOrder;
    if (params.archived) query.archived = params.archived;

    const response = await this.client.get<{
      clients: Account[];
      total: number;
      page: number;
      perPage: number;
      totalAccounts: number;
      archivedCount?: number;
      hiddenCount: number;
      accountLimit: number;
      plan: string;
    }>('/v1/clients', { params: query });
    const d = response.data;
    return {
      accounts: d.clients || [],
      total: d.total ?? (d.clients || []).length,
      page: d.page ?? params.page ?? 1,
      perPage: d.perPage ?? params.perPage ?? 50,
      totalAccounts: d.totalAccounts || 0,
      archivedCount: d.archivedCount || 0,
      hiddenCount: d.hiddenCount || 0,
      accountLimit: d.accountLimit || 0,
      plan: d.plan || 'free',
    };
  }

  /**
   * Fetch every page of accounts (for pickers / dropdowns). Hard-capped to
   * avoid runaway requests on very large orgs.
   */
  async listAllAccounts(maxPages = 20): Promise<Account[]> {
    const perPage = 200;
    const all: Account[] = [];
    for (let page = 1; page <= maxPages; page++) {
      const result = await this.listAccounts({ page, perPage, sortBy: 'createdAt', sortOrder: 'asc' });
      all.push(...result.accounts);
      if (all.length >= result.total || result.accounts.length < perPage) break;
    }
    return all;
  }

  /**
   * Get single account by ID
   */
  async getAccount(accountId: string): Promise<Account> {
    const response = await this.client.get<Account>(`/v1/clients/${encodeURIComponent(accountId)}`);
    return response.data;
  }

  /**
   * Get account history (snapshots over time)
   */
  async getAccountHistory(accountId: string, days: number = 30): Promise<Account[]> {
    const response = await this.client.get<{ snapshots: Account[] }>(
      `/v1/clients/${encodeURIComponent(accountId)}/history`,
      { params: { days } }
    );
    return response.data.snapshots || [];
  }

  /**
   * List events with optional filters
   */
  async listEvents(
    params: {
      accountId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      nextToken?: string;
      eventType?: string;
    },
    options: { signal?: AbortSignal } = {}
  ): Promise<{ events: Event[]; nextToken?: string }> {
    const response = await this.client.get<{ events: Event[]; count: number; nextToken?: string }>(
      '/v1/events',
      { params, signal: options.signal }
    );
    return {
      events: response.data.events || [],
      nextToken: response.data.nextToken,
    };
  }

  // ============================================================================
  // Metrics API
  // ============================================================================

  async getMetrics(): Promise<Metrics> {
    const response = await this.client.get<Metrics>('/v1/metrics');
    return response.data;
  }

  // ============================================================================
  // Account Analytics API
  // ============================================================================

  async getHealthTrend(accountId: string, days: number = 90): Promise<any> {
    const response = await this.client.get(`/v1/clients/${encodeURIComponent(accountId)}/health-trend`, {
      params: { days },
    });
    return response.data;
  }

  async getEntityCohorts(accountId: string, entity: string, action?: string): Promise<any> {
    const params: any = { entity };
    if (action) params.action = action;
    const response = await this.client.get(`/v1/clients/${encodeURIComponent(accountId)}/entity-cohorts`, { params });
    return response.data;
  }

  async getActivityHeatmap(accountId: string, days: number = 90): Promise<any> {
    const response = await this.client.get(`/v1/clients/${encodeURIComponent(accountId)}/activity-heatmap`, {
      params: { days },
    });
    return response.data;
  }

  async getStageHistory(accountId: string): Promise<any> {
    const response = await this.client.get(`/v1/clients/${encodeURIComponent(accountId)}/stage-history`);
    return response.data;
  }

  async getBenchmarks(accountId: string): Promise<any> {
    const response = await this.client.get(`/v1/clients/${encodeURIComponent(accountId)}/benchmarks`);
    return response.data;
  }

  // ============================================================================
  // Echo AI API
  // ============================================================================

  async echoQuery(question: string): Promise<{
    question: string;
    summary: string;
    accounts: Array<{
      accountId: string;
      companyName: string;
      healthScore: number;
      onboardingStage: string;
      daysSinceLastActivity: number;
      createdAt: string;
    }>;
    totalMatched: number;
    queryPlan: any;
  }> {
    const response = await this.client.post('/v1/echo/query', { question });
    return response.data;
  }

  // ============================================================================
  // Client Configuration API
  // ============================================================================

  async getClientConfig(): Promise<ClientConfig> {
    const response = await this.client.get<ClientConfig>('/v1/config');
    return response.data;
  }

  /**
   * Update client configuration.
   * Only the editable settings keys are ever sent; anything else is dropped
   * client-side so the full config object can never be echoed back.
   */
  async updateClientConfig(config: Partial<ClientConfig>): Promise<ClientConfig> {
    const allowed: Array<keyof ClientConfig> = [
      'orgName',
      'industry',
      'entityDefinitions',
      'featureDefinitions',
      'stageConfig',
      'healthScoreConfig',
      'entityFeatureMapping',
      'alertConfig',
    ];
    const payload: Partial<ClientConfig> = {};
    for (const key of allowed) {
      if (key in config && config[key] !== undefined) {
        (payload as any)[key] = config[key];
      }
    }
    const response = await this.client.put<ClientConfig>('/v1/config', payload);
    return response.data;
  }

  // ============================================================================
  // User Management API (authenticated)
  // ============================================================================

  async listUsers(): Promise<{ email: string; firstName: string; lastName: string; status: string; createdAt: string }[]> {
    const response = await this.client.get<{ users: any[] }>('/v1/users');
    return response.data.users || [];
  }

  async inviteUser(data: { email: string; firstName?: string; lastName?: string }): Promise<{ email: string; status: string }> {
    const response = await this.client.post('/v1/users/invite', data);
    return response.data;
  }

  async removeUser(email: string): Promise<void> {
    await this.client.delete(`/v1/users/${encodeURIComponent(email)}`);
  }

  // ============================================================================
  // Registration API (public, no auth)
  // ============================================================================

  /**
   * Check if an email is already registered
   * Returns { exists: boolean, status?: string }
   */
  async checkEmail(email: string): Promise<{ exists: boolean; status?: string }> {
    const response = await axios.get(`${API_URL}/v1/check-email`, {
      params: { email },
      timeout: 15000,
    });
    return response.data;
  }

  /**
   * Register a new organization (self-service signup)
   * Uses raw axios to bypass auth interceptor
   */
  async register(data: {
    email: string;
    password: string;
    orgName: string;
    firstName?: string;
    lastName?: string;
    industry?: string;
    country?: string;
    zipCode?: string;
    tosAcceptedAt?: string;
  }): Promise<{ orgId: string; apiKey: string }> {
    const response = await axios.post(`${API_URL}/v1/register`, data, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    });
    return response.data;
  }

  // ============================================================================
  // Billing API
  // ============================================================================

  async getBilling(): Promise<any> {
    const response = await this.client.get('/v1/billing');
    return response.data;
  }

  async createCheckoutSession(plan: string, interval: 'monthly' | 'annual'): Promise<{ checkoutUrl: string }> {
    const response = await this.client.post('/v1/billing/checkout', { plan, interval });
    return response.data;
  }

  async createBillingPortal(): Promise<{ portalUrl: string }> {
    const response = await this.client.post('/v1/billing/portal');
    return response.data;
  }

  // ============================================================================
  // API Key Management
  // ============================================================================

  /**
   * Fetch the org's API key on demand (never cached in localStorage).
   */
  async getApiKey(): Promise<{ apiKey: string; previousApiKey?: string; previousApiKeyExpiresAt?: string }> {
    const config = await this.getClientConfig();
    return {
      apiKey: config.apiKey,
      previousApiKey: config.previousApiKey,
      previousApiKeyExpiresAt: config.previousApiKeyExpiresAt,
    };
  }

  async regenerateApiKey(): Promise<{ newApiKey: string; oldKeyExpiresAt: string }> {
    const response = await this.client.post('/v1/config/regenerate-api-key');
    return response.data;
  }

  // ============================================================================
  // Admin API
  // ============================================================================

  async adminListOrgs(): Promise<{ orgs: any[]; total: number }> {
    const response = await this.client.get('/v1/admin/orgs');
    return response.data;
  }

  async adminGetOrg(orgId: string): Promise<any> {
    const response = await this.client.get(`/v1/admin/orgs/${encodeURIComponent(orgId)}`);
    return response.data;
  }

  async adminUpdateOrg(orgId: string, updates: Record<string, any>): Promise<any> {
    const response = await this.client.put(`/v1/admin/orgs/${encodeURIComponent(orgId)}`, updates);
    return response.data;
  }
}

// Export singleton instance
export const api = new ApiService();
